import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle } from "lucide-react";
import { Field } from "../Inventory";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Button } from "../../../components/ui/button";
import { DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";
import { SystemSelect, MultiSystemSelect, PartsPicker, BeforePhotosPicker } from "./components";
import { ExternalSupplierPicker } from "./ExternalSupplierPicker";
import { FindSupplierPicker } from "./FindSupplierPicker";
import { PRIORITY, INTERNAL_STATUS, VENDOR_STATUS, CREW_VENDOR_HIDDEN_STATUSES, NO_CANCEL_STATUSES, DEFAULT } from "./constants";

// Field with a small "?" toggle that reveals a one-paragraph explainer —
// so new crew never have to guess what a field is for.
function HintField({ label, hint, testid, className = "", children }) {
  const [open, setOpen] = useState(false);
  return (
    <Field
      className={className}
      label={
        <span className="inline-flex items-center gap-1.5">
          {label}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full text-zinc-400 hover:text-sky-600"
            title="What is this field for?"
            data-testid={`${testid}-btn`}
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </span>
      }
    >
      {children}
      {open && (
        <div className="mt-1 rounded-md border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-800" data-testid={`${testid}-text`}>
          {hint}
        </div>
      )}
    </Field>
  );
}

// The create/edit task form — the inner content of the editor Dialog. Lives
// in its own file (extracted from Maintenance.jsx) so the page component stays
// readable. The Dialog wrapper + orphan-media ref bookkeeping stay in the
// page; this component is purely the form body + footer.
export function TaskEditorBody({
  editingJob, restrictTo, dialogTitle, saveLabel,
  form, setForm, items, inventory, members, suppliers, activeYacht,
  beforeSession, beforeOriginal, purgeOrphans,
  setEditingJob, setOpen, setQuotePrompt, save, saving,
}) {
  const { t } = useTranslation();
  return (
    <>
      <DialogHeader><DialogTitle>{editingJob ? (restrictTo === "routine" ? "Edit periodic task" : "Edit task") : dialogTitle}</DialogTitle>
        <DialogDescription className="sr-only">Fill in the task details — title, category, status, assignee and optional parts, photos and description.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour="task-fields">
        <Field label="Title *" className="md:col-span-2"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="maint-title" /></Field>
        <Field label="Description" className="md:col-span-2"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="maint-desc" /></Field>
        <HintField
          label="Categories"
          testid="cat-help"
          hint="Which yacht system(s) this task belongs to — e.g. Engine Room, Hull, AV / IT. Categories drive filtering, the Reports breakdown and supplier matching when you request quotes."
        >
          <MultiSystemSelect
            value={form.system_categories}
            onChange={(v) => setForm({ ...form, system_categories: v, system_category: v[0] || "" })}
            used={items.flatMap((i) => i.system_categories || (i.system_category ? [i.system_category] : []))}
            testid="maint-system"
          />
        </HintField>
        <HintField
          label="Priority"
          testid="prio-help"
          hint="How urgent this task is for planning — high-priority tasks stand out in the list and reports. It does not change the deadline or any workflow."
        >
          <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="maint-priority">
            {PRIORITY.map((s) => <option key={s}>{s}</option>)}
          </select>
        </HintField>
        <Field label="Status">
          <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="maint-status">
            {(() => {
              // Internal crew task → simple set; vendor → full chain
              // minus the auto/senior-only statuses (approved, prepay_done).
              const full = form.assignee_type === "crew" ? INTERNAL_STATUS : VENDOR_STATUS;
              // Forward-only: once a vendor job reached "approved", no earlier
              // status may be selected. Filtering the full list keeps the
              // canonical order (current status stays at its own slot).
              const curIdx = full.indexOf(form.status);
              const lockBack = form.assignee_type !== "crew" && form.status !== "cancelled" && curIdx >= full.indexOf("approved");
              const noCancel = form.assignee_type !== "crew" && NO_CANCEL_STATUSES.has(form.status);
              const opts = full.filter((s) => {
                if (s === form.status) return true;
                if (form.assignee_type !== "crew" && CREW_VENDOR_HIDDEN_STATUSES.has(s)) return false;
                if (s === "cancelled") return !noCancel;
                if (lockBack && full.indexOf(s) < curIdx) return false;
                return true;
              });
              // Keep the current status visible even if it's outside the
              // set (e.g. editing a legacy vendor job switched to crew).
              const list = opts.includes(form.status) ? opts : [form.status, ...opts];
              return list.map((s) => <option key={s} value={s}>{t(`tasks.status_${s}`)}</option>);
            })()}
          </select>
        </Field>
        <HintField
          label="Deadline"
          testid="deadline-help"
          hint="Your internal deadline — the date this must be handled by. Overdue tracking uses it, and if a supplier's planned start (Work starts) falls after it you'll see an amber warning. The supplier's own dates come from the accepted quote."
        >
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
        </HintField>
        <HintField
          label="Cost"
          testid="cost-help"
          hint="Optional internal cost estimate or budget for this task (e.g. expected parts and labour). For supplier jobs you can leave it empty — the accepted quote's total fills it automatically. It feeds the cost figures in Reports."
        >
          <Input type="number" className="no-spinner" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
        </HintField>
        <Field label="Assignee" className="md:col-span-2">
          <div className="space-y-2">
            <div className="flex gap-1 rounded-md border border-zinc-200 bg-zinc-50 p-1 w-fit text-xs" data-testid="maint-assignee-toggle">
              <button
                type="button"
                onClick={() => setForm({ ...form, assignee_type: "crew", assigned_to: members.some((m) => m.role === "provider" && (m.user_id || m.id) === form.assigned_to) ? "" : form.assigned_to, external_supplier_id: "", external_supplier_name: "", _new_supplier: false, _new_supplier_save: null, status: INTERNAL_STATUS.includes(form.status) ? form.status : "open" })}
                className={`px-3 py-1 rounded ${form.assignee_type === "crew" ? "bg-white shadow-sm font-medium" : "text-zinc-600"}`}
                data-testid="maint-assignee-crew"
              >Crew member</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, assignee_type: "external", assigned_to: "", external_supplier_id: editingJob ? form.external_supplier_id : "", external_supplier_name: editingJob ? form.external_supplier_name : "", _new_supplier: false, _new_supplier_save: null, status: VENDOR_STATUS.includes(form.status) ? form.status : "open" })}
                className={`px-3 py-1 rounded ${form.assignee_type === "external" ? "bg-white shadow-sm font-medium" : "text-zinc-600"}`}
                data-testid="maint-assignee-external"
              >My suppliers</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, assignee_type: "find_supplier", assigned_to: "", external_supplier_id: "", external_supplier_name: "", _new_supplier: false, _new_supplier_save: null, status: VENDOR_STATUS.includes(form.status) ? form.status : "open" })}
                className={`px-3 py-1 rounded ${form.assignee_type === "find_supplier" ? "bg-emerald-600 text-white shadow-sm font-medium" : "text-emerald-700 font-medium"}`}
                data-testid="maint-assignee-find"
              >Find supplier</button>
            </div>
            {form.assignee_type === "crew" ? (
              <select
                className="h-9 w-full rounded-md border border-input px-3 text-sm"
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                data-testid="maint-assigned-to"
              >
                <option value="">Unassigned</option>
                {members.filter((m) => m.role !== "provider").map((m) => (
                  <option key={m.user_id || m.id} value={m.user_id || m.id}>
                    {`${m.name || m.email || "—"} · ${m.role}`}
                  </option>
                ))}
              </select>
            ) : form.assignee_type === "external" ? (
              // "My suppliers": on CREATE it runs the exact Find-supplier quote
              // flow, limited to the yacht's own network. Editing a legacy
              // external job keeps the old direct-assign picker so existing
              // data stays editable.
              editingJob ? (
                <ExternalSupplierPicker form={form} setForm={setForm} suppliers={suppliers} providerMembers={members.filter((m) => m.role === "provider")} />
              ) : (
                <FindSupplierPicker
                  yachtId={activeYacht.id}
                  defaultCategory={form.system_category}
                  selected={form._quote_suppliers}
                  onChange={(sel) => setForm({ ...form, _quote_suppliers: sel })}
                  networkOnly
                />
              )
            ) : (
              <FindSupplierPicker
                yachtId={activeYacht.id}
                defaultCategory={form.system_category}
                selected={form._quote_suppliers}
                onChange={(sel) => setForm({ ...form, _quote_suppliers: sel })}
              />
            )}
            {form.assignee_type === "external" && editingJob && (
              <div className="text-[11px] text-zinc-500">
                Use this for vendors who aren't on Seavanto. You move the job through statuses
                on their behalf, and an invoice will be auto-created when you mark the job confirmed
                (if you attach an invoice document).
              </div>
            )}
          </div>
        </Field>
        <div className="md:col-span-2">
          <PartsPicker
            parts={form.parts}
            inventory={inventory}
            onChange={(parts) => setForm({ ...form, parts })}
          />
        </div>
        <div className="md:col-span-2">
          <BeforePhotosPicker
            photos={form.before_photos}
            yacht_id={activeYacht.id}
            onChange={(before_photos) => setForm({ ...form, before_photos })}
            onUploadPath={(p) => beforeSession.current.add(p)}
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => {
            // Cancel = discard this session's uploaded-but-unsaved media.
            const orphans = [...beforeSession.current];
            beforeSession.current = new Set();
            beforeOriginal.current = [];
            if (orphans.length) purgeOrphans(orphans);
            setEditingJob(null); setForm(DEFAULT); setOpen(false);
          }}
          disabled={saving}
          data-testid="cancel-maint"
        >Cancel</Button>
        <Button
          onClick={() => {
            const sel = form._quote_suppliers || [];
            if (!editingJob && ["find_supplier", "external"].includes(form.assignee_type) && sel.length > 0) {
              // The track-offline question only concerns OFFLINE suppliers —
              // online suppliers always get their quote request notification.
              if (sel.some((s) => s.mode !== "online")) setQuotePrompt(true);
              else save({ sendQuotes: true });
            } else {
              save();
            }
          }}
          className="bg-ink hover:bg-black"
          disabled={!form.title || saving}
          data-testid="save-maint"
        >{saving ? "Saving…" : (editingJob ? "Save changes" : saveLabel)}</Button>
      </DialogFooter>
    </>
  );
}
